import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './App.css';
import hjemfaldData from './data/Hjemfaldspligt.json';
import geocodeCache from './data/Hjemfaldspligt.geocoded.json';

const INITIAL_CENTER = [55.6961, 12.4683];
const DENMARK_BOUNDS = [
  [54.6, 8.4],
  [57.9, 14.5],
];
const CURRENT_YEAR = new Date().getFullYear();
const CLUSTER_GRID_PX = 54;

const URGENCY_META = {
  expired: { label: `Udløber snart (<= ${CURRENT_YEAR})`, color: '#b21f35' },
  critical: { label: `Tæt på (${CURRENT_YEAR + 1}-${CURRENT_YEAR + 5})`, color: '#db5f23' },
  soon: { label: `Snart (${CURRENT_YEAR + 6}-${CURRENT_YEAR + 15})`, color: '#f2a33c' },
  later: { label: `Senere (>= ${CURRENT_YEAR + 16})`, color: '#2b9348' },
};

const houseIconCache = new Map();
const clusterIconCache = new Map();

function normalizeAddress(address) {
  if (!address || typeof address !== 'string') {
    return '';
  }

  const clean = address.replace(/\s+/g, ' ').trim();
  if (!clean || clean.toLowerCase() === 'ingen adresse') {
    return '';
  }

  return clean;
}

function getUrgencyKey(year) {
  if (year <= CURRENT_YEAR) {
    return 'expired';
  }
  if (year <= CURRENT_YEAR + 5) {
    return 'critical';
  }
  if (year <= CURRENT_YEAR + 15) {
    return 'soon';
  }
  return 'later';
}

function getHouseIcon(year) {
  const key = getUrgencyKey(year);
  const color = URGENCY_META[key].color;

  if (houseIconCache.has(color)) {
    return houseIconCache.get(color);
  }

  const houseSvg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 10.8 12 3l9 7.8" stroke="#1a3146" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 9.9V21h12V9.9" fill="#f9f5ef" stroke="#1a3146" stroke-width="1.8"/><path d="M10.1 21v-5.7h3.8V21" fill="#ffd59e" stroke="#1a3146" stroke-width="1.4"/><path d="M3 10.8 12 3l9 7.8" stroke="${color}" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
  );

  const icon = L.icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${houseSvg}`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -20],
  });

  houseIconCache.set(color, icon);
  return icon;
}

function getClusterIcon(avgYear, count) {
  const urgency = getUrgencyKey(avgYear);
  const color = URGENCY_META[urgency].color;
  const countBucket = count < 10 ? 's' : count < 50 ? 'm' : 'l';
  const cacheKey = `${color}-${countBucket}-${count}`;

  if (clusterIconCache.has(cacheKey)) {
    return clusterIconCache.get(cacheKey);
  }

  const icon = L.divIcon({
    className: 'cluster-wrap',
    html: `<div class="cluster-marker" style="--cluster-color: ${color}"><span>${count}</span></div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });

  clusterIconCache.set(cacheKey, icon);
  return icon;
}

function createPopupContent(point) {
  const container = document.createElement('div');
  container.className = 'popup-content';

  const address = document.createElement('p');
  address.className = 'popup-title';
  address.textContent = point.address;

  const year = document.createElement('p');
  year.textContent = `Hjemfaldsår: ${point.year}`;

  const urgency = document.createElement('p');
  urgency.textContent = `Kategori: ${URGENCY_META[getUrgencyKey(point.year)].label}`;

  const matrikel = document.createElement('p');
  matrikel.textContent = `Matrikelnr.: ${point.matrikel}`;

  const ejerlav = document.createElement('p');
  ejerlav.textContent = `Ejerlav: ${point.ejerlav || 'Ukendt'}`;

  container.append(address, year, urgency, matrikel, ejerlav);
  return container;
}

function createClusterPopup(cluster) {
  const years = cluster.map((point) => point.year);
  const min = Math.min(...years);
  const max = Math.max(...years);
  const avg = years.reduce((sum, value) => sum + value, 0) / years.length;

  const container = document.createElement('div');
  container.className = 'popup-content';

  const title = document.createElement('p');
  title.className = 'popup-title';
  title.textContent = `${cluster.length} adresser i dette område`;

  const range = document.createElement('p');
  range.textContent = `Hjemfaldsår: ${min} - ${max}`;

  const urgency = document.createElement('p');
  urgency.textContent = `Dominerende niveau: ${URGENCY_META[getUrgencyKey(avg)].label}`;

  container.append(title, range, urgency);
  return container;
}

function clusterPoints(points, map) {
  if (!points.length) {
    return [];
  }

  const zoom = map.getZoom();
  const buckets = new Map();

  for (const point of points) {
    const pixel = map.project([point.lat, point.lng], zoom);
    const x = Math.floor(pixel.x / CLUSTER_GRID_PX);
    const y = Math.floor(pixel.y / CLUSTER_GRID_PX);
    const bucketKey = `${x}:${y}`;

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, []);
    }

    buckets.get(bucketKey).push(point);
  }

  return Array.from(buckets.values());
}

function App() {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [mapZoom, setMapZoom] = useState(10);

  const records = useMemo(() => {
    return hjemfaldData
      .map((item, index) => {
        const year = Number(item['Hjemfaldsår']);
        const normalizedAddress = normalizeAddress(item.Adresse);

        return {
          id: `${item['Matrikelnr.'] ?? 'na'}-${item['El.lejl.nr.'] ?? 'na'}-${index}`,
          address: normalizedAddress,
          year: Number.isFinite(year) ? year : null,
          matrikel: String(item['Matrikelnr.'] ?? 'Ukendt'),
          ejerlav: item.Ejerlav ? String(item.Ejerlav) : '',
        };
      })
      .filter((row) => row.address && row.year !== null);
  }, []);

  const years = useMemo(() => records.map((row) => row.year), [records]);
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);

  const [yearRange, setYearRange] = useState([minYear, maxYear]);

  const filteredRecords = useMemo(() => {
    return records.filter(
      (row) => row.year >= yearRange[0] && row.year <= yearRange[1]
    );
  }, [records, yearRange]);

  const uniqueAddresses = useMemo(() => {
    return Array.from(new Set(records.map((row) => row.address)));
  }, [records]);

  const resolvedAddressCount = useMemo(() => {
    return uniqueAddresses.filter((address) => {
      const coords = geocodeCache[address];
      return coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lng));
    }).length;
  }, [uniqueAddresses]);

  const unresolvedAddressCount = uniqueAddresses.length - resolvedAddressCount;

  const mappedPoints = useMemo(() => {
    return filteredRecords
      .map((row) => {
        const coords = geocodeCache[row.address];
        if (!coords) {
          return null;
        }

        const lat = Number(coords.lat);
        const lng = Number(coords.lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null;
        }

        return {
          ...row,
          lat,
          lng,
        };
      })
      .filter(Boolean);
  }, [filteredRecords]);

  const urgencyStats = useMemo(() => {
    return mappedPoints.reduce(
      (acc, point) => {
        acc[getUrgencyKey(point.year)] += 1;
        return acc;
      },
      { expired: 0, critical: 0, soon: 0, later: 0 }
    );
  }, [mappedPoints]);

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) {
      return;
    }

    const map = L.map(mapContainerRef.current, {
      center: INITIAL_CENTER,
      zoom: 10,
      minZoom: 7,
      maxBounds: DENMARK_BOUNDS,
      maxBoundsViscosity: 0.7,
    });

    L.tileLayer('https://tiles.jfmtech.dk/styles/osm-bright/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors, Tiles &copy; JFM',
    }).addTo(map);

    map.on('zoomend', () => {
      setMapZoom(map.getZoom());
    });

    markersLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setMapZoom(map.getZoom());

    return () => {
      map.remove();
      mapRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = markersLayerRef.current;

    if (!map || !layer) {
      return;
    }

    const clusters = clusterPoints(mappedPoints, map);

    layer.clearLayers();

    for (const cluster of clusters) {
      if (cluster.length === 1) {
        const point = cluster[0];
        const marker = L.marker([point.lat, point.lng], { icon: getHouseIcon(point.year) });
        marker.bindPopup(createPopupContent(point), { maxWidth: 320, minWidth: 250 });
        marker.addTo(layer);
        continue;
      }

      const centerLat =
        cluster.reduce((sum, point) => sum + point.lat, 0) / cluster.length;
      const centerLng =
        cluster.reduce((sum, point) => sum + point.lng, 0) / cluster.length;
      const avgYear =
        cluster.reduce((sum, point) => sum + point.year, 0) / cluster.length;

      const marker = L.marker([centerLat, centerLng], {
        icon: getClusterIcon(avgYear, cluster.length),
      });

      marker.on('click', () => {
        if (map.getZoom() < 17) {
          map.flyTo([centerLat, centerLng], map.getZoom() + 2, { duration: 0.4 });
        }
      });

      marker.bindPopup(createClusterPopup(cluster), { maxWidth: 320, minWidth: 240 });
      marker.addTo(layer);
    }

  }, [mappedPoints, mapZoom]);

  return (
    <main className="app-shell">
      <aside className="panel">
        <h1>Hjemfaldspligt i Danmark</h1>
        <p className="lead">
          Kort over adresser med hjemfaldspligt. Markører er farvekodet efter hjemfaldsår.
        </p>

        <div className="stats">
          <p>Datapunkter (filtreret): {filteredRecords.length}</p>
          <p>Viste punkter/klynger: {mappedPoints.length}</p>
          <p>Geokodede adresser: {resolvedAddressCount}/{uniqueAddresses.length}</p>
          <p>Uafklarede adresser: {unresolvedAddressCount}</p>
        </div>

        <div className="urgency-legend">
          <h2>Hjemfaldsårs tidshorizont</h2>
          {Object.entries(URGENCY_META).map(([key, meta]) => (
            <div key={key} className="legend-row">
              <span className="legend-dot" style={{ '--legend-color': meta.color }} />
              <span>{meta.label}</span>
              <strong>{urgencyStats[key]}</strong>
            </div>
          ))}
        </div>

        <div className="filter-group">
          <h2>Filter på hjemfaldsår</h2>
          <label>
            Fra
            <input
              type="number"
              min={minYear}
              max={yearRange[1]}
              value={yearRange[0]}
              onChange={(event) => {
                const value = Number(event.target.value);
                setYearRange(([, to]) => [Math.min(value, to), to]);
              }}
            />
          </label>

          <label>
            Til
            <input
              type="number"
              min={yearRange[0]}
              max={maxYear}
              value={yearRange[1]}
              onChange={(event) => {
                const value = Number(event.target.value);
                setYearRange(([from]) => [from, Math.max(value, from)]);
              }}
            />
          </label>
        </div>
      </aside>

      <section className="map-section">
        <div ref={mapContainerRef} className="map" aria-label="Danmarkskort" />
      </section>
    </main>
  );
}

export default App;
